import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Lock } from 'phosphor-react';
import {
  formatDateLong,
  getCurrencyDigits,
  sanitizeTestId,
} from './optionsPerformanceUtils';
import { useOptionsLocale } from '../../context/OptionsLanguageContext';
import { formatLocalizedNumber } from './optionsLocaleFormatting';
import { useOptionsEntitlements } from '../../context/OptionsEntitlementsContext';

// ── helpers ────────────────────────────────────────────────────────────────────

const pctClass = (v) => {
  if (v == null) return 'text-white/55';
  if (v > 0.0001) return 'text-[#4ADE80]';
  if (v < -0.0001) return 'text-[#EF4444]';
  return 'text-white/55';
};

const fmtPct = (v) => {
  if (v == null) return '—';
  const abs = formatLocalizedNumber(Math.abs(v * 100), { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${v >= 0 ? '+' : '-'}${abs}%`;
};

const fmtBtc = (v) => v == null ? '—' : `₿ ${formatLocalizedNumber(v, { minimumFractionDigits: 4, maximumFractionDigits: 6 })}`;
const fmtUsd = (v) => v == null ? '—' : `US$ ${formatLocalizedNumber(v, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtNative = (v, currency) => currency === 'ETH'
  ? (v == null ? '—' : `${formatLocalizedNumber(v, { minimumFractionDigits: 4, maximumFractionDigits: 6 })} ETH`)
  : fmtBtc(v);

const fmtShortDate = (isoStr) => {
  if (!isoStr) return '—';
  const d = new Date(isoStr);
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' }).toUpperCase();
};

const fmtTime = (isoStr) => {
  if (!isoStr) return '';
  const d = new Date(isoStr);
  return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
};

const directionLabel = (dir) => {
  if (!dir) return '';
  return dir.toLowerCase() === 'sell' ? 'SHORT' : 'LONG';
};

// ── CAPITAL ANTES DO INÍCIO ────────────────────────────────────────────────────

const PreTradeCashFlows = ({ cashFlows, isPortuguese }) => {
  const [expanded, setExpanded] = useState(false);
  if (!cashFlows || cashFlows.length === 0) return null;

  const label = isPortuguese ? 'Capital Antes do Início' : 'Capital Before Start';
  const viewLabel = isPortuguese ? 'Ver movimentações' : 'View movements';
  const hideLabel = isPortuguese ? 'Ocultar' : 'Hide';

  const cfTypeLabel = (type) => {
    if (!type) return type;
    if (type === 'deposit') return isPortuguese ? 'Depósito' : 'Deposit';
    if (type === 'withdrawal') return isPortuguese ? 'Saque' : 'Withdrawal';
    return type;
  };

  return (
    <div className="mt-3 rounded-xl border border-white/10 bg-black/20 p-3" data-testid="pre-trade-cash-flows">
      <button
        type="button"
        className="flex w-full items-center justify-between gap-2 text-left"
        onClick={() => setExpanded((e) => !e)}
        data-testid="pre-trade-cash-flows-toggle"
      >
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-white/40">{label}</p>
          <p className="mt-0.5 text-xs text-white/60">
            {cashFlows.length} {isPortuguese ? 'movimentações' : 'movements'}
          </p>
        </div>
        <div className="flex items-center gap-1.5 rounded-full border border-white/10 px-2 py-1">
          <span className="text-[10px] font-semibold text-white/45">{expanded ? hideLabel : viewLabel}</span>
          {expanded
            ? <ChevronUp size={12} className="text-white/40" />
            : <ChevronDown size={12} className="text-white/40" />
          }
        </div>
      </button>

      {expanded && (
        <div className="mt-3 space-y-1.5" data-testid="pre-trade-cash-flows-list">
          {cashFlows.map((cf, i) => (
            <div key={cf.id || i} className="flex items-center justify-between gap-2 py-1 border-t border-white/5 first:border-0" data-testid={`pre-trade-cf-${i}`}>
              <div>
                <p className="text-[10px] font-semibold text-white/45 uppercase tracking-wide">
                  {fmtShortDate(cf.timestamp)}
                </p>
                <p className="text-xs text-white/55">{cfTypeLabel(cf.type)}</p>
              </div>
              <p className="text-xs font-semibold tabular-nums text-[#60A5FA]">
                {cf.amount_native != null ? `+${formatLocalizedNumber(cf.amount_native, { minimumFractionDigits: 4, maximumFractionDigits: 8 })}` : '—'}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ── PRIMEIRA OPERAÇÃO ──────────────────────────────────────────────────────────

const FirstOperation = ({ period, isPortuguese }) => {
  const at = period.v2_first_exposure_at;
  const instrument = period.v2_first_exposure_instrument;
  const direction = period.v2_first_exposure_direction;
  const qty = period.v2_first_exposure_quantity;

  if (!at) return null;

  const label = isPortuguese ? 'Primeira Operação' : 'First Operation';
  const dirLabel = directionLabel(direction);

  return (
    <div className="mt-3 rounded-xl border border-[#22C55E]/15 bg-[#22C55E]/5 px-3 py-2.5" data-testid="first-operation">
      <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[#A7F3D0]/60">{label}</p>
      <p className="mt-1 text-xs font-semibold text-white/80">
        {fmtShortDate(at)}
        {fmtTime(at) ? <span className="ml-1.5 text-white/45">· {fmtTime(at)}</span> : null}
      </p>
      {instrument && (
        <p className="mt-0.5 text-xs text-white/55 tabular-nums font-mono">
          {instrument}
          {dirLabel && <span className="ml-2 text-[10px] font-semibold uppercase tracking-wide text-white/40">{dirLabel}</span>}
          {qty != null && <span className="ml-2 text-[10px] text-white/35">{formatLocalizedNumber(qty, { maximumFractionDigits: 4 })}</span>}
        </p>
      )}
    </div>
  );
};

// ── PERÍODO ATUAL ──────────────────────────────────────────────────────────────

const CurrentPeriodCard = ({ period, currency, isPortuguese, testIdBase }) => {
  const ce = period.combined_equity;
  const hasCombined = ce && ce.initial_combined_btc != null && ce.current_combined_btc != null;
  const preTradeCFs = period.v2_accumulated_cash_flows_before_start || [];
  const hasPreTrade = preTradeCFs.length > 0;

  return (
    <div
      className="rounded-[22px] border border-[#22C55E]/20 bg-gradient-to-b from-[#22C55E]/5 to-transparent p-4"
      data-testid={`${testIdBase}-current`}
    >
      {/* header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[#A7F3D0]/60" data-testid={`${testIdBase}-current-label`}>
            {isPortuguese ? 'Período Atual' : 'Current Period'}
          </p>
          <p className="mt-1 text-sm text-white/70" data-testid={`${testIdBase}-current-dates`}>
            {formatDateLong(period.started_at)} → {isPortuguese ? 'Atual' : 'Current'}
          </p>
        </div>
        <span className="rounded-full border border-[#22C55E]/25 bg-[#22C55E]/10 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-[#A7F3D0]" data-testid={`${testIdBase}-current-badge`}>
          {isPortuguese ? 'Ativo' : 'Active'}
        </span>
      </div>

      {/* equity grid */}
      {hasCombined ? (
        <div className="mt-3 grid grid-cols-2 gap-2.5">
          <div className="rounded-xl border border-white/8 bg-black/25 px-3 py-2.5" data-testid={`${testIdBase}-initial-equity`}>
            <p className="text-[9px] font-semibold uppercase tracking-[0.14em] text-white/35">
              {isPortuguese ? 'Equity Inicial' : 'Initial Equity'}
            </p>
            <p className="mt-1 text-sm font-bold text-white tabular-nums">{fmtBtc(ce.initial_combined_btc)}</p>
            <p className="mt-0.5 text-[11px] text-white/45 tabular-nums">{fmtUsd(ce.initial_combined_usd)}</p>
          </div>
          <div className="rounded-xl border border-white/8 bg-black/25 px-3 py-2.5" data-testid={`${testIdBase}-current-equity`}>
            <p className="text-[9px] font-semibold uppercase tracking-[0.14em] text-white/35">
              {isPortuguese ? 'Equity Atual' : 'Current Equity'}
            </p>
            <p className="mt-1 text-sm font-bold text-white tabular-nums">{fmtBtc(ce.current_combined_btc)}</p>
            <p className="mt-0.5 text-[11px] text-white/45 tabular-nums">{fmtUsd(ce.current_combined_usd)}</p>
          </div>
        </div>
      ) : (
        <div className="mt-3 grid grid-cols-2 gap-2.5">
          <div className="rounded-xl border border-white/8 bg-black/25 px-3 py-2.5" data-testid={`${testIdBase}-initial-equity`}>
            <p className="text-[9px] font-semibold uppercase tracking-[0.14em] text-white/35">
              {isPortuguese ? 'Equity Inicial' : 'Initial Equity'}
            </p>
            <p className="mt-1 text-sm font-bold text-white tabular-nums">{fmtNative(period.starting_equity_native, currency)}</p>
            <p className="mt-0.5 text-[11px] text-white/45 tabular-nums">{fmtUsd(period.starting_equity_usd)}</p>
          </div>
          <div className="rounded-xl border border-white/8 bg-black/25 px-3 py-2.5" data-testid={`${testIdBase}-current-equity`}>
            <p className="text-[9px] font-semibold uppercase tracking-[0.14em] text-white/35">
              {isPortuguese ? 'Equity Atual' : 'Current Equity'}
            </p>
            <p className="mt-1 text-sm font-bold text-white tabular-nums">{fmtNative(period.ending_equity_native, currency)}</p>
            <p className="mt-0.5 text-[11px] text-white/45 tabular-nums">{fmtUsd(period.ending_equity_usd)}</p>
          </div>
        </div>
      )}

      {/* retorno */}
      <div className="mt-2.5 rounded-xl border border-white/8 bg-black/20 px-3 py-2.5" data-testid={`${testIdBase}-returns`}>
        <p className="text-[9px] font-semibold uppercase tracking-[0.14em] text-white/35">
          {isPortuguese ? 'Retorno' : 'Return'}
        </p>
        <div className="mt-1.5 flex gap-5">
          {hasCombined ? (
            <>
              <div className="flex items-center gap-1.5" data-testid={`${testIdBase}-btc-return`}>
                <span className="text-[10px] font-semibold text-white/40">BTC</span>
                <span className={`text-sm font-bold tabular-nums ${pctClass(ce.btc_return_pct)}`}>{fmtPct(ce.btc_return_pct)}</span>
              </div>
              <div className="flex items-center gap-1.5" data-testid={`${testIdBase}-usd-return`}>
                <span className="text-[10px] font-semibold text-white/40">USD</span>
                <span className={`text-sm font-bold tabular-nums ${pctClass(ce.usd_return_pct)}`}>{fmtPct(ce.usd_return_pct)}</span>
              </div>
            </>
          ) : (
            <span className={`text-sm font-bold tabular-nums ${pctClass(period.return_pct)}`}>{fmtPct(period.return_pct)}</span>
          )}
        </div>
      </div>

      {/* pre-trade CFs + first operation */}
      {hasPreTrade && (
        <PreTradeCashFlows cashFlows={preTradeCFs} isPortuguese={isPortuguese} />
      )}
      <FirstOperation period={period} isPortuguese={isPortuguese} />
    </div>
  );
};

// ── PERÍODO ANTERIOR (collapsible) ────────────────────────────────────────────

const PreviousPeriodCard = ({ period, currency, periodIndex, isPortuguese, testIdBase }) => {
  const [open, setOpen] = useState(false);
  const ce = period.combined_equity;
  const hasCombined = ce && ce.initial_combined_btc != null;

  const startDate = fmtShortDate(period.started_at);
  const endDate = fmtShortDate(period.ended_at);
  const btcReturn = hasCombined ? ce.btc_return_pct : period.return_pct;
  const usdReturn = hasCombined ? ce.usd_return_pct : null;

  return (
    <div
      className="rounded-[18px] border border-white/8 bg-black/15 overflow-hidden"
      data-testid={`${testIdBase}-prev-${periodIndex}`}
    >
      <button
        type="button"
        className="flex w-full items-center justify-between gap-3 px-3.5 py-3 text-left"
        onClick={() => setOpen((o) => !o)}
        data-testid={`${testIdBase}-prev-${periodIndex}-toggle`}
      >
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-white/35">
            {startDate} → {endDate}
          </p>
          <div className="mt-0.5 flex items-center gap-3">
            {btcReturn != null ? (
              <span className={`text-xs font-bold tabular-nums ${pctClass(btcReturn)}`}>
                BTC {fmtPct(btcReturn)}
              </span>
            ) : null}
            {usdReturn != null ? (
              <span className={`text-xs tabular-nums ${pctClass(usdReturn)}`}>
                USD {fmtPct(usdReturn)}
              </span>
            ) : null}
          </div>
        </div>
        {open
          ? <ChevronUp size={14} className="shrink-0 text-white/35" />
          : <ChevronDown size={14} className="shrink-0 text-white/35" />
        }
      </button>

      {open && (
        <div className="border-t border-white/6 px-3.5 pb-3.5 pt-3 space-y-2" data-testid={`${testIdBase}-prev-${periodIndex}-details`}>
          {hasCombined ? (
            <>
              <Row label={isPortuguese ? 'Equity Inicial' : 'Initial Equity'} value={`${fmtBtc(ce.initial_combined_btc)}  ·  ${fmtUsd(ce.initial_combined_usd)}`} />
              <Row label={isPortuguese ? 'Equity Final' : 'Ending Equity'} value={`${fmtBtc(ce.current_combined_btc)}  ·  ${fmtUsd(ce.current_combined_usd)}`} />
            </>
          ) : (
            <>
              <Row label={isPortuguese ? 'Equity Inicial' : 'Initial Equity'} value={fmtNative(period.starting_equity_native, currency)} />
              <Row label={isPortuguese ? 'Equity Final' : 'Ending Equity'} value={fmtNative(period.ending_equity_native, currency)} />
            </>
          )}
          {period.closing_cash_flow_type && (
            <Row label={isPortuguese ? 'Encerrado por' : 'Closed by'} value={period.closing_cash_flow_type} />
          )}
        </div>
      )}
    </div>
  );
};

const Row = ({ label, value }) => (
  <div className="flex items-start justify-between gap-3">
    <span className="text-xs text-white/40 shrink-0">{label}</span>
    <span className="text-xs font-semibold text-white/75 text-right tabular-nums">{value}</span>
  </div>
);

// ── MAIN SECTION ───────────────────────────────────────────────────────────────

export const OptionsCapitalPeriodsSection = ({ sections }) => {
  const { t, isPortuguese } = useOptionsLocale();
  const { isPro, openUpgradeModal } = useOptionsEntitlements();

  const renderLockedOverlay = () => (
    <div className="absolute inset-0 z-10 flex flex-col items-center justify-center p-6 text-center bg-[#09090B]/85 rounded-2xl backdrop-blur-[2px]">
      <div className="mx-auto max-w-sm space-y-4">
        <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-[#A855F7]/10 text-[#A855F7] border border-[#A855F7]/20">
          <Lock size={22} weight="fill" />
        </div>
        <div>
          <p className="text-base font-bold text-white">Disponível no GENI PRO 💜</p>
          <p className="mt-1 text-xs text-white/55 leading-relaxed">Monitore seus períodos de capital, taxas acumuladas e retornos estruturados.</p>
        </div>
        <Button 
          onClick={() => openUpgradeModal("PERFORMANCE")}
          className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white font-bold text-xs px-5 py-2.5 rounded-xl transition-all"
        >
          DESBLOQUEAR ACESSO PRO
        </Button>
      </div>
    </div>
  );

  return (
    <div className="space-y-4 relative" data-testid="options-capital-periods-section">
      <div className="space-y-1">
        <p className="text-sm font-semibold uppercase tracking-[0.16em] text-white/45" data-typography="label" data-testid="options-capital-periods-label">
          {t('performance.capitalPeriods')}
        </p>
        <h2 className="text-2xl font-black tracking-tight text-white flex items-center gap-2" data-typography="heading" data-testid="options-capital-periods-title">
          {t('performance.capitalPeriodsTitle')}
          {!isPro && (
            <span className="inline-flex items-center gap-1 rounded-full bg-[#A855F7]/10 text-[#A855F7] border border-[#A855F7]/20 px-2 py-0.5 text-xs font-semibold">
              <Lock size={11} weight="fill" /> PRO
            </span>
          )}
        </h2>
        <p className="max-w-3xl text-sm text-white/60" data-testid="options-capital-periods-description">
          {t('performance.capitalPeriodsDescription')}
        </p>
      </div>

      <div 
        onClick={() => { if (!isPro) openUpgradeModal("PERFORMANCE"); }}
        className={`relative ${!isPro ? 'cursor-pointer' : ''}`}
      >
        <div className={`grid gap-4 xl:grid-cols-2 ${!isPro ? 'blur-[4px] pointer-events-none select-none min-h-[250px]' : ''}`} data-testid="options-capital-periods-grid">
          {sections.map((section) => {
            const safeCurrency = sanitizeTestId(section.currency);
            const testIdBase = `options-cp-${safeCurrency}`;

            const openPeriod = section.periods.find((p) => p.status === 'OPEN');
            const closedPeriods = section.periods
              .filter((p) => p.status !== 'OPEN')
              .sort((a, b) => (b.started_timestamp_ms || 0) - (a.started_timestamp_ms || 0));

            return (
              <Card
                key={section.currency}
                className="border-white/10 bg-white/[0.03] text-white shadow-none"
                data-testid={`options-capital-periods-card-${safeCurrency}`}
              >
                <CardContent className="space-y-3 p-4 sm:p-5">
                  {/* currency header */}
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <p className="text-2xl font-black tracking-tight text-white" data-typography="metric" data-testid={`${testIdBase}-currency-title`}>
                      {section.currency}
                    </p>
                    {openPeriod && (
                      <span className="rounded-full border border-[#22C55E]/20 bg-[#22C55E]/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-[#A7F3D0]" data-testid={`${testIdBase}-active-badge`}>
                        {t('performance.currentActivePeriod')}
                      </span>
                    )}
                  </div>

                  {!section.periods.length ? (
                    <div className="rounded-2xl border border-dashed border-white/10 bg-black/20 px-4 py-6 text-sm text-white/60" data-testid={`${testIdBase}-empty`}>
                      {section.emptyMessage}
                    </div>
                  ) : (
                    <>
                      {/* PERÍODO ATUAL */}
                      {openPeriod && (
                        <CurrentPeriodCard
                          period={openPeriod}
                          currency={section.currency}
                          isPortuguese={isPortuguese}
                          testIdBase={testIdBase}
                        />
                      )}

                      {/* PERÍODOS ANTERIORES */}
                      {closedPeriods.length > 0 && (
                        <div className="space-y-2" data-testid={`${testIdBase}-previous-periods`}>
                          <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-white/35 px-1">
                            {isPortuguese ? 'Períodos Anteriores' : 'Previous Periods'}
                          </p>
                          {closedPeriods.map((p, idx) => (
                            <PreviousPeriodCard
                              key={p.id}
                              period={p}
                              currency={section.currency}
                              periodIndex={p.period_index || idx}
                              isPortuguese={isPortuguese}
                              testIdBase={testIdBase}
                            />
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
        {!isPro && renderLockedOverlay()}
      </div>
    </div>
  );
};

export default OptionsCapitalPeriodsSection;
